import React, { useState } from 'react'
import { useAtom } from 'jotai'
import { createUseStyles } from 'react-jss'
import { CaseDetail, File } from '../../../../models/CaseDetail'
import { colors } from '../../../../theme/palette'
import { Button } from '../../../ui/Button/Button'
import { Typography } from '../../../ui/Typography/Typography'
import { Overlay } from '../../../ui/Sidebar'
import { RemindersHeaderInfo } from '../Reminders/Reminders'
import { cardProps, control } from '../../../../theme/reuseables'
import CaseFileCreateView from './CaseFileCreate'
import { L } from '../../../providers/Lokalise'
import { CaseFilesRowCard } from './CaseFilesRowCard'
import { profileAtom } from '../../../../api/profile/profile'

type CaseNotesProps = {
    caseDetails: CaseDetail
    files: File[]
    onUpdated?: () => void
}

const useStyles = createUseStyles({
    control: {
        ...control,
    },
    card: {
        ...cardProps.card,
    },
    title: {
        ...cardProps.title,
    },
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: '100%',
        paddingBottom: '80px',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100%',
        transform: 'translateX(100%)',
        transition: 'transform 0.3s ease-in-out',
        '&.visible': {
            transform: 'translateX(0)',
        },
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
})

const CaseFilesOverlay: React.FC<CaseNotesProps> = ({
    caseDetails,
    files,
    onUpdated,
}): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false)
    const classes = useStyles()
    const [authProfile] = useAtom(profileAtom)
    const [view, setView] = useState<'list' | 'create'>('list')

    const caseFilesListView = (): JSX.Element => {
        return (
            <>
                <div
                    className={`${classes.contentWrapper} ${
                        isOpen ? 'visible' : 'visible'
                    }`}
                >
                    <Typography
                        tag="h2"
                        size="XL"
                        align="center"
                        weight="bold"
                        style={{ margin: '20px 0 10px' }}
                    >
                        {L('LØN_SAGS_FILER_SIDEBAR_TITEL')}
                    </Typography>
                    {!caseDetails?.isClosed && (
                        <div
                            style={{
                                marginBottom: '2rem',
                                marginTop: '2rem',
                                display: 'flex',
                                justifyContent: 'end',
                            }}
                        >
                            <Button
                                onClick={() => setView('create')}
                                size="medium"
                                disabled={
                                    !caseDetails.allowedUpdateActionList.includes(
                                        'Sagsbehandle'
                                    ) || authProfile.isImitator
                                }
                            >
                                {L('LØN_SAGS_FIL_SIDEBAR_TILFØJ_FIL_KNAP')}
                            </Button>
                        </div>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem',
                            marginBottom: '2rem',
                        }}
                    >
                        {files.map((file) => (
                            <CaseFilesRowCard
                                key={file.fullName}
                                file={file}
                                onUpdated={() => {
                                    onUpdated?.()
                                }}
                                caseDetails={caseDetails}
                            />
                        ))}
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}
            >
                <Button
                    type="action"
                    onClick={() => setIsOpen(true)}
                    size="xlarge"
                    style={{
                        fontSize: '.9rem',
                        minHeight: '1px',
                        textDecoration: 'underline',
                    }}
                >
                    {L('LØN_SAGS_FIL_SIDEBAR_VIS_ALLE_KNAP')}
                </Button>
            </div>

            <Overlay
                isShowing={isOpen}
                hide={() => {
                    setIsOpen(false)
                }}
            >
                <RemindersHeaderInfo
                    name={caseDetails?.newlyHired.name}
                    caseNumber={caseDetails?.caseNumber}
                />
                {view === 'list' && caseFilesListView()}
                {view === 'create' && (
                    <CaseFileCreateView
                        caseDetails={caseDetails}
                        onCancel={() => {
                            setView('list')
                        }}
                        onUpdated={() => {
                            setView('list')
                            onUpdated?.()
                        }}
                    />
                )}
            </Overlay>
        </>
    )
}

export default CaseFilesOverlay
